import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { filter } from 'rxjs/operators';
import { StorageService } from './storage.service';

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        dataLayer?: any[];
    }
}

export type AnalyticsSource = 'web' | 'app';
export type AnalyticsConsent = 'granted' | 'denied';

/** Where the person's yes or no is kept on the phone. */
const CONSENT_KEY = 'analytics_consent';

/**
 * Usage statistics, on the web and in the store apps, only with consent.
 *
 * On the web the cookie dialog in index.html decides: gtag only exists after
 * "Accept", so every call here does nothing until then.
 *
 * In the apps Firebase is built in with collection switched off (the Android
 * manifest and the iOS Info.plist), and stays off until the person answers yes
 * to the question asked on first open; Account lets them change their mind.
 * Saying no also deletes what Firebase holds on the phone.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {

    private initialised = false;
    private readonly platform: 'web' | 'ios' | 'android';
    private readonly source: AnalyticsSource;
    private consent: AnalyticsConsent | null = null;

    constructor(private storage: StorageService, private router: Router) {
        const p = Capacitor.getPlatform();
        this.platform = (p === 'ios' || p === 'android') ? p : 'web';
        this.source = this.platform === 'web' ? 'web' : 'app';
    }

    /** Called once on start-up, after storage is ready. */
    async init(): Promise<void> {
        if (this.initialised) return;
        this.initialised = true;

        if (this.isNative()) {
            const stored = await this.storage.get(CONSENT_KEY);
            this.consent = stored === 'granted' || stored === 'denied' ? stored : null;
            await this.applyConsent();

            this.router.events
                .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
                .subscribe(e => this.setCurrentScreen(this.screenName(e.urlAfterRedirects)));
            return;
        }

        await this.setUserProperty('platform', this.platform);
        await this.setUserProperty('source', this.source);
    }

    isNative(): boolean {
        return this.platform !== 'web';
    }

    /** True in the store apps until the person has answered the question. */
    needsChoice(): boolean {
        return this.isNative() && this.consent === null;
    }

    isGranted(): boolean {
        return this.consent === 'granted';
    }

    async setConsent(granted: boolean): Promise<void> {
        if (!this.isNative()) return;
        this.consent = granted ? 'granted' : 'denied';
        this.storage.set(CONSENT_KEY, this.consent);
        await this.applyConsent();

        if (granted) {
            this.setCurrentScreen(this.screenName(this.router.url));
        } else {
            try {
                await FirebaseAnalytics.resetAnalyticsData();
            } catch (err) {
                console.warn('[analytics] resetAnalyticsData failed', err);
            }
        }
    }

    private async applyConsent(): Promise<void> {
        try {
            await FirebaseAnalytics.setEnabled({ enabled: this.consent === 'granted' });
        } catch (err) {
            console.warn('[analytics] setEnabled failed', err);
        }

        if (this.consent === 'granted') {
            await this.setUserProperty('platform', this.platform);
            await this.setUserProperty('source', this.source);
        }
    }

    /** Nothing leaves the phone without a yes. */
    private mayCollect(): boolean {
        return this.platform === 'web' || this.consent === 'granted';
    }

    getSource(): AnalyticsSource {
        return this.source;
    }

    getPlatform(): 'web' | 'ios' | 'android' {
        return this.platform;
    }

    async logEvent(name: string, params: Record<string, any> = {}): Promise<void> {
        if (!this.mayCollect()) return;
        const merged = { source: this.source, platform: this.platform, ...params };
        try {
            if (this.platform === 'web') {
                if (typeof window.gtag === 'function') {
                    window.gtag('event', name, merged);
                }
                return;
            }
            await FirebaseAnalytics.logEvent({ name, params: merged });
        } catch (err) {
            console.warn(`[analytics] logEvent(${name}) failed`, err);
        }
    }

    async setUserProperty(key: string, value: string | null | undefined): Promise<void> {
        if (!this.mayCollect()) return;
        const v = value == null ? '' : String(value);
        try {
            if (this.platform === 'web') {
                if (typeof window.gtag === 'function') {
                    window.gtag('set', 'user_properties', { [key]: v });
                }
                return;
            }
            await FirebaseAnalytics.setUserProperty({ key, value: v });
        } catch (err) {
            console.warn(`[analytics] setUserProperty(${key}) failed`, err);
        }
    }

    async setUserId(userId: string | null): Promise<void> {
        if (!this.mayCollect()) return;
        try {
            if (this.platform === 'web') {
                if (typeof window.gtag === 'function') {
                    window.gtag('set', { user_id: userId ?? undefined });
                }
                return;
            }
            await FirebaseAnalytics.setUserId({ userId: userId ?? '' });
        } catch (err) {
            console.warn('[analytics] setUserId failed', err);
        }
    }

    async getClientId(): Promise<string | null> {
        try {
            if (this.platform === 'web') {
                return this.readGaClientIdFromCookie();
            }
            if (!this.mayCollect()) return null;
            const { appInstanceId } = await FirebaseAnalytics.getAppInstanceId();
            return appInstanceId ?? null;
        } catch (err) {
            console.warn('[analytics] getClientId failed', err);
            return null;
        }
    }

    private readGaClientIdFromCookie(): string | null {
        if (typeof document === 'undefined') return null;
        const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
        if (!match) return null;
        const raw = decodeURIComponent(match[1]);
        const parts = raw.split('.');
        return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
    }

    /** The path the web reports as its page, without the query string. */
    private screenName(url: string): string {
        return (url || '/').split(/[?#]/)[0] || '/';
    }

    async setCurrentScreen(screenName: string) {
        if (!this.mayCollect()) return;
        if (this.isNative()) {
            try {
                await FirebaseAnalytics.setCurrentScreen({ screenName, screenClassOverride: screenName });
            } catch (err) {
                console.warn('[analytics] setCurrentScreen failed', err);
            }
            return;
        }
        await this.logEvent('screen_view', {
            screen_name: screenName,
            screen_class: screenName,
        });
    }
}
